import GenerateDoc from "./GenerateDoc";
import React, { useEffect, useState } from "react";
import { loadRelationsSettings, listenRelationRows } from "../services/data";

export default function MultiRelationFilter({ user, selectedModele, onGenerateBatch}) {
  const [relations, setRelations] = useState([]);
  const [filters, setFilters] = useState({ type: "Tous", q: "" });
  const [relFields, setRelFields] = useState([]);
  const [page, setPage] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState({ running: false, current: 0 });
 
  useEffect(() => {
    if (!user?.uid) return;
    let unsubs = [];
    (async () => {
      const settings = await loadRelationsSettings(user.uid);
      const types = settings?.types || [];
      setRelFields(settings?.fields || []);
      // Écoutes Firestore par type
      const acc = {};
      types.forEach((t) => {
        const unsub = listenRelationRows(user.uid, t.key, (rows) => {
          acc[t.key] = rows.map(r => ({
            ...r,
            typeKey: t.key,
            typeLabel: t.label || t.key,
          }));
          // recompose la liste à chaque update
          const merged = Object.values(acc).flat();
          setRelations(merged);
        });
        unsubs.push(unsub);
      });
    })();
    return () => { unsubs.forEach(u => { try { u(); } catch{} }); };
  }, [user?.uid]);

  // Types disponibles
  const typeOptions = ["Tous", ...Array.from(new Set(relations.map((r) => r.typeLabel)))];

  // Filtrage
  const filtered = relations.filter((r) => {
    const matchType = filters.type === "Tous" || r.typeLabel === filters.type;
    const matchSearch =
      !filters.q ||
      Object.values(r || {})
        .join(" ")
        .toLowerCase()
        .includes(filters.q.toLowerCase());
    return matchType && matchSearch;
  });

  // Colonnes dynamiques basées sur rel_fields
  const dynamicColumns = relFields.map((f) => ({ key: f.key, name: f.name }));

  // Pagination (3 lignes par page)
  const totalPages = Math.max(1, Math.ceil(filtered.length / 3));
  const paginated = filtered.slice((page - 1) * 3, page * 3);

  // Supprimer une ligne
  const handleRemove = (relation) => {
    const newRelations = relations.filter((r) => r !== relation);
    setRelations(newRelations);

    // Mise à jour dans localStorage
    const key = `relations_data_${relation.typeKey}`;
    const old = JSON.parse(localStorage.getItem(key)) || [];
    const updated = old.filter((r) => JSON.stringify(r) !== JSON.stringify(relation));
    localStorage.setItem(key, JSON.stringify(updated));
  };

  // Génération batch
  const handleGenerate = async () => {
    if (!selectedModele) {
      alert("[warning] Aucun modèle sélectionné !");
      return;
    }
    if (filtered.length === 0) {
      alert("[warning] Aucune relation à générer !");
      return;
    }

    setProgress({ running: true, current: 0 });

    for (let i = 0; i < filtered.length; i++) {
      setProgress({ running: true, current: i + 1 });
      const relation = filtered[i];
      await new Promise((resolve) => {
        // Génération du doc avec GenerateDoc
        GenerateDoc({
          templateArrayBuffer: selectedModele.storagePath, // [warning] vérifie selon ton modèle
          values: relation,
          tagKeys: Object.keys(relation),
          requiredKeys: [],
          outputNameTemplate: `${relation.client_nom || "Doc"}_${i + 1}.docx`,
          onAfterGenerate: () => resolve(),
        });
      });
    }

    setProgress({ running: false, current: 0 });
    alert("[ok] Génération terminée !");
  };


  return (
    <div className="relation-filter">
      {/* Section filtres */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Type de relation</label>
            <select
              className="filter-select"
              value={filters.type}
              onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
            >
              {typeOptions.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Recherche</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Rechercher une relation..."
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            />
          </div>
          <div className="filter-group">
            <button
              className="btn btn-primary"
              onClick={() => {
                if (!selectedModele) {
                  console.warn("[warning] Aucun modèle sélectionné !");
                  return;
                }
                //console.log("[ok] Bouton Générer cliqué !");
                //console.log("Relations à générer:", relations);
                //console.log("Modèle sélectionné:", selectedModele);

                onGenerateBatch(relations, selectedModele);
              }}
            >
              Générer
            </button>                      
          </div>
        </div>
      </div>

      {/* Tableau avec pagination */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Type</th>
              {dynamicColumns.map((col) => (
                <th key={col.key}>{col.name}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((r, idx) => (
              <tr key={idx}>
                <td>{r.typeLabel}</td>
                {dynamicColumns.map((col) => (
                  <td key={col.key}>{r[col.key] || "-"}</td>
                ))}
                <td className="table-actions">
                  <button
                    onClick={() => handleRemove(r)}
                    className="icon-btn danger"
                    title="Supprimer"
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td colSpan={dynamicColumns.length + 2} style={{ textAlign: "center", color: "#888" }}>
                  Aucune relation trouvée
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination footer */}
        <div className="table-footer">
          <span>
            Page {page} sur {totalPages}
          </span>
          <div className="pagination">
            <div
              className={`pagination-btn ${page === 1 ? "disabled" : ""}`}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fas fa-chevron-left"></i>
            </div>
            <div
              className={`pagination-btn ${page === totalPages ? "disabled" : ""}`}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fas fa-chevron-right"></i>
            </div>
          </div>
        </div>
        {progress.running && (
          <div style={{ margin: "10px 0", fontWeight: "bold", color: "#007bff" }}>
            Ligne {progress.current} / {filtered.length} en cours...
          </div>
        )}
      </div>
    </div>
  );
}
